import Pagination from 'components/Blocks/Pagination/Pagination';
import ProductsList, { ProductListTypes } from 'components/Blocks/Product/List/ProductsList';
import { categoryDetailContentMessageTwClass } from 'components/Pages/CategoryDetail/CategoryDetailProductsWrapper';
import { mapListedProductConnectionType } from 'connectors/products/Products';
import { BrandProductsQueryDocumentApi } from 'graphql/generated';
import { useProductsData } from 'helpers/loadMore/useProductsData';
import { getUrlWithoutGetParameters } from 'helpers/parsing/urlParsing';
import { useCurrentLoadMoreQuery } from 'helpers/queryParams/useCurrentLoadMoreQuery';
import { useCurrentPageQuery } from 'helpers/queryParams/useCurrentPageQuery';
import { useGtmBrandProductListView } from 'hooks/gtm/useGtmBrandProductListView';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useDomainConfig } from 'hooks/useDomainConfig';
import Trans from 'next-translate/Trans';
import { useRouter } from 'next/router';
import React, { RefObject, useEffect, useState } from 'react';
import { BrandDetailType } from 'types/brand';

type BrandDetailProductsWrapperProps = {
    brand: BrandDetailType;
    paginationScrollTargetRef: RefObject<HTMLDivElement>;
};

export const BrandDetailProductsWrapper: FC<BrandDetailProductsWrapperProps> = ({
    brand,
    paginationScrollTargetRef,
}) => {
    const t = useTypedTranslationFunction();
    const [shouldRefetch, setShouldRefetch] = useState(false);
    const { asPath } = useRouter();

    const currentPage = useCurrentPageQuery();
    const currentLoadMore = useCurrentLoadMoreQuery();
    const { currencyCode } = useDomainConfig();
    const totalCount = brand.productConnection.totalCount;

    const { products, areProductsFetching, isLoadingMoreProducts } = useProductsData(
        BrandProductsQueryDocumentApi,
        totalCount,
        shouldRefetch,
    );
    const brandProducts = products ? mapListedProductConnectionType(products, currencyCode).products : [];
    useGtmBrandProductListView(brand, getUrlWithoutGetParameters(asPath), brandProducts, areProductsFetching);

    useBroadcastChannel({
        name: 'orderCompleteBroadcast',
        callBack: () => {
            setShouldRefetch(true);
        },
    });

    useEffect(() => {
        if (shouldRefetch) {
            setShouldRefetch(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldRefetch]);

    return (
        <>
            {(brandProducts.length !== 0 || areProductsFetching) && (
                <ProductsList
                    currentPage={currentPage}
                    gtmListName="brand"
                    isLoading={areProductsFetching || isLoadingMoreProducts}
                    products={brandProducts}
                    type={ProductListTypes.Normal}
                    isLoadMore={currentLoadMore > 0}
                />
            )}
            {brandProducts.length !== 0 && (
                <Pagination
                    paginationScrollTargetRef={paginationScrollTargetRef}
                    isLoading={isLoadingMoreProducts}
                    isWithLoadMore
                    totalCount={totalCount}
                />
            )}
            {totalCount === 0 && !areProductsFetching && (
                <div className={categoryDetailContentMessageTwClass}>
                    <div>
                        <strong>{t('No results match the filter')}</strong>
                    </div>
                    <div>
                        <Trans i18nKey="ProductsNoResults" components={{ 0: <br /> }} />
                    </div>
                </div>
            )}
        </>
    );
};
